import { requestGQL } from 'common/graphQlClient';
import { useOfflineMutation } from 'common/offline/useOfflineMutation';
import { isTestEnv, oneHourInMs } from 'config';
import { useQuery } from 'react-query';

import {
  GET_PROPERTY_FIBER_CHECKLIST,
  GetPropertyFiberChecklistResponse,
  PropertyFiberChecklist,
  CREATE_FIBER_PROPERTY_CHECKLIST,
  UPDATE_FIBER_PROPERTY_CHECKLIST_BY_PROPERTY_ID,
} from '../api';

type UseFiberChecklistOptions = {
  onCreateSuccess?: () => void;
  onUpdateSuccess?: () => void;
  onOfflineQueued?: () => void;
};

export const useFiberChecklist = (propertyId: number | null, opts: UseFiberChecklistOptions = {}) => {
  const { data, isLoading, isError } = useQuery<GetPropertyFiberChecklistResponse | null>(
    ['propertyFiberChecklist', propertyId],
    () => requestGQL(GET_PROPERTY_FIBER_CHECKLIST, { propertyId }),
    {
      enabled: propertyId != null,
      refetchOnWindowFocus: false,
      retry: isTestEnv ? 0 : 3,
      staleTime: oneHourInMs,
    },
  );

  const updateMutation = useOfflineMutation<unknown, unknown, Partial<PropertyFiberChecklist>>({
    mutationFn: (fiberChecklistPatch) =>
      requestGQL(UPDATE_FIBER_PROPERTY_CHECKLIST_BY_PROPERTY_ID, { propertyId, fiberChecklistPatch }),
    mutationName: 'updateFiberPropertyChecklistByPropertyId',
    entity: 'fiberChecklist',
    operationType: 'UPDATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [
      ['propertyById', propertyId],
      ['propertyFiberChecklist', propertyId],
    ],
    onOfflineQueued: opts.onOfflineQueued,
    onSuccess: opts.onUpdateSuccess,
  });

  const createMutation = useOfflineMutation<unknown, unknown, Partial<PropertyFiberChecklist>>({
    mutationFn: (checklist) => requestGQL(CREATE_FIBER_PROPERTY_CHECKLIST, { propertyId, checklist }),
    mutationName: 'createFiberPropertyChecklist',
    entity: 'fiberChecklist',
    operationType: 'CREATE',
    propertyId: propertyId ?? undefined,
    invalidateKeys: [
      ['propertyById', propertyId],
      ['propertyFiberChecklist', propertyId],
    ],
    onOfflineQueued: opts.onOfflineQueued,
    onSuccess: opts.onCreateSuccess,
  });

  return {
    data,
    isLoading,
    isError,
    updatePropertyFiberChecklist: updateMutation.mutate,
    isErrorUpdatePropertyFiberChecklist: updateMutation.isError,
    createPropertyFiberChecklist: createMutation.mutate,
    isErrorCreatePropertyFiberChecklist: createMutation.isError,
    isMutationsLoading: createMutation.isLoading || updateMutation.isLoading,
    isOnline: updateMutation.isOnline,
    pendingCount: updateMutation.pendingCount,
  };
};
